import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  FinancialCategoryInterface,
  FinancialEntryInterface
} from "../../common/interfaces";
import {
  FinancialEntryType,
  FinancialEntryTypeLabel,
  FinancialPaymentMethod,
  FinancialPaymentMethodLabel
} from "../../components/enum/financialEnums";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  categories: FinancialCategoryInterface[];
  entry?: FinancialEntryInterface | null;
  /**
   * Empresa escolhida pelo ADMIN na tela. O lançamento precisa nascer na
   * empresa selecionada, não na do ADMIN. Perfis comuns não mandam nada: o
   * backend ignora este campo e usa a empresa do token.
   */
  companyId?: number | null;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const FinancialEntryCreateModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  categories,
  entry,
  companyId
}) => {
  const [typeFilter, setTypeFilter] = useState<FinancialEntryType>(
    FinancialEntryType.EXPENSE
  );
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [accrualDate, setAccrualDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [settleNow, setSettleNow] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<FinancialPaymentMethod | "">("");

  const updateMode = Boolean(entry);
  const containerId = "toaster-financial-entry-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (entry) {
      setTypeFilter(entry.type as FinancialEntryType);
      setCategoryId(entry.category_id);
      setDescription(entry.description);
      setAmount(String(entry.amount));
      setAccrualDate(entry.accrual_date?.slice(0, 10) ?? todayIso());
      setDueDate(entry.due_date?.slice(0, 10) ?? "");
      setNotes(entry.notes ?? "");
      setSettleNow(false);
      setPaymentMethod("");
    } else {
      setTypeFilter(FinancialEntryType.EXPENSE);
      setCategoryId("");
      setDescription("");
      setAmount("");
      setAccrualDate(todayIso());
      setDueDate("");
      setNotes("");
      setSettleNow(false);
      setPaymentMethod("");
    }
  }, [entry, open]);

  const filteredCategories = categories.filter((c) => c.type === typeFilter);

  const handleSubmit = async () => {
    try {
      if (!categoryId) {
        autoError("Selecione uma categoria.");
        return;
      }
      if (!description.trim()) {
        autoError("Informe a descrição.");
        return;
      }
      const amountNumber = Number(amount.replace(",", "."));
      if (!amountNumber || amountNumber <= 0) {
        autoError("Informe um valor válido.");
        return;
      }
      if (settleNow && !paymentMethod) {
        autoError("Selecione a forma de pagamento para liquidar agora.");
        return;
      }

      const payload: any = {
        category_id: categoryId,
        description: description.trim(),
        amount: amountNumber,
        accrual_date: accrualDate,
        due_date: dueDate || undefined,
        notes: notes.trim() || undefined
      };

      if (settleNow) {
        payload.settled_at = accrualDate;
        payload.payment_method = paymentMethod;
      }

      if (updateMode && entry) {
        await api.patch(`/financial/entries/${entry.financial_entry_id}`, payload);
        autoSuccess("Lançamento atualizado com sucesso!");
      } else {
        await api.post("/financial/entries", {
          ...payload,
          company_id: companyId ?? undefined
        });
        autoSuccess("Lançamento criado com sucesso!");
      }

      onSaved();
      onClose();
    } catch (error) {
      handleHttpError(error);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 440,
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h2>{updateMode ? "Editar lançamento" : "Novo lançamento"}</h2>

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as FinancialEntryType);
                setCategoryId("");
              }}
            >
              {Object.values(FinancialEntryType).map((t) => (
                <MenuItem key={t} value={t}>
                  {FinancialEntryTypeLabel[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Categoria</InputLabel>
            <Select
              label="Categoria"
              value={categoryId}
              onChange={(e) => setCategoryId(Number(e.target.value))}
            >
              {filteredCategories.map((c) => (
                <MenuItem key={c.financial_category_id} value={c.financial_category_id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Valor (R$)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Competência"
            type="date"
            value={accrualDate}
            onChange={(e) => setAccrualDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Vencimento (opcional)"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Observações"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />

          {!updateMode && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Já foi liquidado?</InputLabel>
              <Select
                label="Já foi liquidado?"
                value={settleNow ? "yes" : "no"}
                onChange={(e) => setSettleNow(e.target.value === "yes")}
              >
                <MenuItem value="no">Não, fica pendente</MenuItem>
                <MenuItem value="yes">Sim, já entrou/saiu do caixa</MenuItem>
              </Select>
            </FormControl>
          )}

          {!updateMode && settleNow && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Forma de pagamento</InputLabel>
              <Select
                label="Forma de pagamento"
                value={paymentMethod}
                onChange={(e) =>
                  setPaymentMethod(e.target.value as FinancialPaymentMethod)
                }
              >
                {Object.values(FinancialPaymentMethod).map((m) => (
                  <MenuItem key={m} value={m}>
                    {FinancialPaymentMethodLabel[m]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              {updateMode ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FinancialEntryCreateModal;
